import { formatDateMMDDyyyy } from '../../util/DateUtils';
import type { AssistantAudience } from './assistantCapabilities';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeLink, KnowledgeQuery } from './knowledgeRegistry';
import {
  searchBuyerCheckoutHistory,
  searchBuyerCurrentInventory,
  searchSupplierCheckoutHistory,
  searchSupplierCurrentInventory,
  type SupplierCheckoutLookupRow,
  type SupplierCurrentInventoryLookupRow,
} from './requests/equipment-lookup-requests';

const CHECKOUT_HISTORY_BY_AUDIENCE: Partial<
  Record<AssistantAudience, (equipmentNumber: string) => Promise<SupplierCheckoutLookupRow[]>>
> = {
  supplier: searchSupplierCheckoutHistory,
  buyer: searchBuyerCheckoutHistory,
};

const CURRENT_INVENTORY_BY_AUDIENCE: Partial<
  Record<AssistantAudience, (equipmentNumber: string) => Promise<SupplierCurrentInventoryLookupRow[]>>
> = {
  supplier: searchSupplierCurrentInventory,
  buyer: searchBuyerCurrentInventory,
};

const EQUIPMENT_TOKEN = '[a-z0-9-]*\\d[a-z0-9-]*';
const NUMBER_AFTER_KEYWORD = new RegExp(
  `(?:container|equipment|asset|trailer|chassis)\\s*#?\\s*(${EQUIPMENT_TOKEN})`,
  'i',
);
const BARE_NUMBER = new RegExp(`\\b(${EQUIPMENT_TOKEN})\\b`, 'i');

const normalize = (value?: string): string => (value ?? '').trim().toLowerCase();

const formatDate = (value?: string): string => {
  if (!value) return 'an unknown date';
  const formatted = formatDateMMDDyyyy(value);
  return formatted || value;
};

const inventoryLink = (equipmentNumber: string, audience: AssistantAudience): KnowledgeLink => {
  const encoded = encodeURIComponent(equipmentNumber);
  // The buyer report reads its initial filter from `initEquipmentNumber`
  // (see BuyerInventoryReport.jsx); the supplier report reads plain
  // `equipmentNumber` (see SupplierInventoryReport.jsx / SupplierCurrentInventoryReport.tsx).
  return audience === 'buyer'
    ? { label: 'Open Current Inventory', path: `/buyer-inventory?initEquipmentNumber=${encoded}` }
    : { label: 'Open Current Inventory', path: `/supplier-inventory?equipmentNumber=${encoded}` };
};

const interchangeLink = (equipmentNumber: string, audience: AssistantAudience): KnowledgeLink => {
  const encoded = encodeURIComponent(equipmentNumber);
  // The buyer report reads `initEquipmentNumber` and has no activity filter at
  // all (see BuyerInterChangesReport.jsx), so `&activity=CHECK_OUT` would be
  // silently ignored there; the supplier report supports both `equipmentNumber`
  // and `activity` (see SupplierInterchangeReport.tsx).
  return audience === 'buyer'
    ? { label: 'Open Interchanges', path: `/buyer-interchanges?initEquipmentNumber=${encoded}` }
    : { label: 'Open Interchanges', path: `/supplier-interchanges?equipmentNumber=${encoded}&activity=CHECK_OUT` };
};

const rowMatches = (
  row: Pick<SupplierCheckoutLookupRow, 'containerNumber' | 'trailerNumber' | 'chassisNumber'>,
  equipmentNumber: string,
): boolean =>
  [row.containerNumber, row.trailerNumber, row.chassisNumber].some(
    (candidate) => normalize(candidate) === normalize(equipmentNumber),
  );

const currentRowMatches = (
  row: Pick<SupplierCurrentInventoryLookupRow, 'containerNumber' | 'trailerNumber' | 'chassisNumber'>,
  equipmentNumber: string,
): boolean =>
  [row.containerNumber, row.trailerNumber, row.chassisNumber].some(
    (candidate) => normalize(candidate) === normalize(equipmentNumber),
  );

/**
 * Pulls the equipment number from the user's message.
 *
 * Prefers a number directly tied to an equipment keyword ("container 22222222"),
 * falling back to the last long-enough token for shorter phrasings like
 * "when did 22222222 check out?".
 */
export const extractEquipmentNumber = (message: string): string | null => {
  const keyed = message.match(NUMBER_AFTER_KEYWORD);
  if (keyed) return keyed[1];

  const matches = Array.from(message.matchAll(new RegExp(`\\b(${EQUIPMENT_TOKEN})\\b`, 'gi'))).map(
    (match) => match[1],
  );
  if (matches.length > 0) return matches[matches.length - 1];

  const bare = message.match(BARE_NUMBER);
  return bare?.[1] ?? null;
};

const checkoutAnswer = (
  equipmentNumber: string,
  row: SupplierCheckoutLookupRow,
  audience: AssistantAudience,
): KnowledgeAnswer => ({
  text:
    `The latest recorded checkout for **${equipmentNumber}** was **${formatDate(row.activityDate)}**` +
    `${row.locationName ? ` at **${row.locationName}**` : ''}` +
    `${row.orderNumber ? ` on booking **#${row.orderNumber}**` : ''}.`,
  link: interchangeLink(equipmentNumber, audience),
});

const dwellingAnswer = (
  equipmentNumber: string,
  row: SupplierCurrentInventoryLookupRow,
  audience: AssistantAudience,
): KnowledgeAnswer => ({
  text:
    `I do not see a checkout recorded for **${equipmentNumber}**. It is still dwelling` +
    `${row.locationName ? ` at **${row.locationName}**` : ' in your current inventory'}` +
    `${row.checkInDate ? ` since **${formatDate(row.checkInDate)}**` : ''}` +
    `${row.orderNumber ? ` on booking **#${row.orderNumber}**` : ''}.`,
  link: inventoryLink(equipmentNumber, audience),
});

const answer = async ({ message, audience }: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const equipmentNumber = extractEquipmentNumber(message);

  if (!equipmentNumber) {
    return {
      text:
        'Which equipment number do you mean? For example, ask _"When was container 22222222 checked out?"_ and I will look for its latest checkout.',
    };
  }

  const searchCheckoutHistory = CHECKOUT_HISTORY_BY_AUDIENCE[audience] ?? searchSupplierCheckoutHistory;
  const searchCurrentInventory = CURRENT_INVENTORY_BY_AUDIENCE[audience] ?? searchSupplierCurrentInventory;

  const checkoutRows = await searchCheckoutHistory(equipmentNumber);
  const checkoutMatch = checkoutRows.find((row) => rowMatches(row, equipmentNumber)) ?? checkoutRows[0];
  if (checkoutMatch) return checkoutAnswer(equipmentNumber, checkoutMatch, audience);

  const currentRows = await searchCurrentInventory(equipmentNumber);
  const currentMatch =
    currentRows.find((row) => currentRowMatches(row, equipmentNumber)) ?? currentRows[0];
  if (currentMatch) return dwellingAnswer(equipmentNumber, currentMatch, audience);

  return {
    text:
      `I could not find **${equipmentNumber}** in your checkout history or current inventory. ` +
      'Double-check the equipment number, or open the Interchanges report filtered to that number.',
    link: interchangeLink(equipmentNumber, audience),
  };
};

export const equipmentCheckoutQuery: KnowledgeQuery = {
  id: 'equipmentCheckoutLookup',
  audiences: ['supplier', 'buyer'],
  synonyms: [
    'when was container checked out',
    'when was equipment checked out',
    'when did container check out',
    'when did equipment check out',
    'when did container leave',
    'when did equipment leave',
    'when was trailer checked out',
    'when was chassis checked out',
  ],
  requires: [
    ['when'],
    ['container', 'equipment', 'asset', 'trailer', 'chassis'],
    ['checked', 'checkout', 'out', 'leave', 'left'],
  ],
  example: 'when was container 22222222 checked out',
  description:
    'Looks up when a specific piece of equipment last checked out by equipment number, for ' +
    'suppliers or buyers. Reads Interchanges for the latest CHECK_OUT event and, if there is ' +
    'none, falls back to Current Inventory to say the equipment is still dwelling at a yard. ' +
    'Distinct from the current-inventory count skill, which returns only how many pieces are ' +
    'on site.',
  answer,
};

export const __testing = {
  extractEquipmentNumber,
  rowMatches,
  currentRowMatches,
};
